'use client';
import { useCart } from '@/contexts/CartContext';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export default function CartOverlay() {
  const { items, isCartOpen, closeCart, updateQuantity, removeFromCart, subtotal } = useCart();
  const router = useRouter();

  const handleCheckout = () => {
    closeCart();
    router?.push('/checkout');
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 transition-opacity"
        onClick={closeCart}
      />
      {/* Cart Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border z-50 shadow-2xl flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-serif font-bold text-foreground">Your Cart</h2>
          <button
            onClick={closeCart}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-background hover:bg-muted border border-border transition-colors"
            aria-label="Close cart"
          >
            <Icon name="XMarkIcon" size={24} className="text-foreground" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {items?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Icon name="ShoppingBagIcon" size={48} className="text-muted-foreground mb-4" />
              <p className="text-lg text-muted-foreground">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-6">
              {items?.map((item) => (
                <div key={item?.id} className="flex gap-4">
                  {/* Product Image */}
                  <div className="w-20 h-20 bg-background rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item?.image}
                      alt={item?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">{item?.name}</h3>
                      <p className="text-sm text-primary font-bold">£{item?.price}</p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item?.id, item?.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-background transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Icon name="MinusIcon" size={16} className="text-foreground" />
                      </button>
                      <span className="text-foreground font-medium w-8 text-center">{item?.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item?.id, item?.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-background transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Icon name="PlusIcon" size={16} className="text-foreground" />
                      </button>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFromCart(item?.id)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Remove item"
                  >
                    <Icon name="TrashIcon" size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items?.length > 0 && (
          <div className="border-t border-border p-6 space-y-4">
            {/* Subtotal */}
            <div className="flex items-center justify-between text-lg">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-bold text-foreground">£{subtotal?.toFixed(2)}</span>
            </div>

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              className="w-full bg-primary text-primary-foreground py-4 rounded-full text-lg font-bold hover:bg-foreground hover:text-background transition-colors shadow-lg"
            >
              Proceed to Checkout
            </button>

            {/* Continue Shopping - Enhanced visibility */}
            <button
              onClick={closeCart}
              className="w-full py-3 rounded-full border border-border text-foreground hover:bg-background transition-colors text-base font-medium"
            >
              ← Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}